import CircularProgress from '@material-ui/core/CircularProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import Downshift from 'downshift';
import PropTypes from 'prop-types';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import Errored from '../core/Errored';
import TitledPanel from '../core/TitledPanel';
import { sanitiseText } from '../core/utils/utils';
import GS from '../grafsync/grafsync';
import { globalStyles as styles } from './style/gstheme';
import { createLengthWarning, debounce, makeCancelable } from './utils/utils';

class SearchDropDown extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isOpen: false,
            isSearching: false,
            results: [],
            searchID: null,
            text: "",
            isErrored: null
        };
        this.textInput = React.createRef();
        this.cancellable = null;
    }

    find = (value, lang) => {
        if (this.state.isChosen) return;
        this.setState({ isSearching: true }, () => {
            try {
                this.doAutocomplete(this.props.search(value, lang))
                    .promise
                    .then((data) => {
                        if (this.cancellable && this.cancellable.isCancelled()) return;
                        // Ignore 'old' 
                        if (data.uuid && data.uuid !== this.state.searchID) return;
                        this.setState({
                            text: "",
                            isSearching: false,
                            results: data.results,
                            isOpen: data.results && data.results.length > 0 ? true : false
                        });
                        if ((!data.results || data.results.length === 0) && this.props.onNoResults) {
                            this.props.onNoResults(value);
                        }
                    })
                    .catch((e) => {
                        if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
                    });
            }
            catch (e) {
                if (!(this.cancellable) || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
            }
        });
    };

    handleChange = (text) => {
        if (this.props.onClearResults) this.props.onClearResults(this.state.isChosen);
        if (text.trim().length > 1) {
            if (text !== this.state.text) {
                this.setState({ text, results: [], isChosen: false, isOpen: false });
                debounce(this.find, this, [text, this.props.lang]);
            }
        }
    };

    doAutocomplete(payload) {
        const searchID = uuidv4();
        this.setState({ searchID });
        this.cancellable = makeCancelable(GS.autocomplete(payload, searchID));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    render() {

        const { results, isOpen, isSearching, isErrored } = this.state;
        const { classes, lang, title, listSelectionDisplay, listDisplay, errorText, suppressMessages, dataTest, maxLength,
            sortProp, sortByLength, listSecondIcon, margin, listIcon, chosen, focus, isClickableResult, wrapped } = this.props;

        if (isErrored) return <Errored dataTest="bulk-error" data={isErrored} />;

        return (
            <Downshift
                initialIsOpen={false}
                isOpen={isOpen}
                onOuterClick={() => this.setState({
                    isOpen: false,
                    results: [],
                    isChosen: true
                })}
                stateReducer={(state, changes) => {
                    switch (changes.type) {
                        case Downshift.stateChangeTypes.changeInput:
                            if (changes.inputValue.indexOf("\t")) {
                                return {
                                    inputValue: sanitiseText(changes.inputValue)
                                };
                            }
                            return {};
                        default:
                            return changes;
                    }
                }
                }
                onInputValueChange={this.handleChange}
                onChange={selection => {
                    this.setState({
                        isOpen: false,
                        results: [],
                        isChosen: selection
                    }, () => {
                        if (this.props.onHandleSelectionChange) this.props.onHandleSelectionChange();
                        const chosenResult = selection && selection.id ? selection.id : null;
                        return chosenResult ? chosen(chosenResult, selection) : null;
                    });
                }}
                itemToString={item => {
                    if (item) {
                        // If a selection function is supplied use that or else fallback to result display function
                        if (listSelectionDisplay) {
                            return listSelectionDisplay(item, lang);
                        }
                        else return listDisplay(item, lang);
                    }
                    else return '';
                }}
            >
                {({
                    getInputProps,
                    getItemProps,
                    getMenuProps,
                    inputValue,
                    highlightedIndex,
                    selectedItem,
                    clearSelection
                }) => {
                    const field =
                        <TextField
                            style={{ width: '100%', marginTop: '0px' }}
                            margin="dense"
                            autoFocus={focus}
                            inputRef={this.textInput}
                            type="text"
                            value={inputValue}
                            error={errorText !== null}
                            placeholder={title}
                            variant="outlined"
                            {...getInputProps()}
                            helperText={!suppressMessages ?
                                errorText ?
                                    <span data-test="helper-text">{errorText}</span>
                                    :
                                    createLengthWarning(inputValue, maxLength)
                                :
                                null
                            }
                            InputLabelProps={{
                                className: classes.inputLabel,
                                classes: {
                                    shrink: classes.inputLabelShrink
                                }
                            }}
                            InputProps={
                                {
                                    className: classes.inputLabel,
                                    inputProps: {
                                        "data-test": dataTest + "-input",
                                        maxLength
                                    },
                                    endAdornment:
                                        <InputAdornment position="end">
                                            {selectedItem ?
                                                <Tooltip title="Clear">
                                                    <span>
                                                        <ClearIcon className={classes.inputIconHighlighted} onClick={
                                                            () => {
                                                                clearSelection();
                                                                this.textInput && this.textInput.current && this.textInput.current.focus();
                                                            }
                                                        } />
                                                    </span>
                                                </Tooltip>
                                                :
                                                (isSearching ?
                                                    <Tooltip title="Searching for results">
                                                        <span>
                                                            <CircularProgress color="secondary" size={20} />
                                                        </span>
                                                    </Tooltip>
                                                    :
                                                    <Tooltip title="Search">
                                                        <span>
                                                            <SearchIcon data-test="search-icon" className={classes.inputIconHighlighted} />
                                                        </span>
                                                    </Tooltip>
                                                )
                                            }
                                        </InputAdornment>
                                }
                            } />;

                    return (
                        <div>
                            {wrapped ?
                                <TitledPanel title="Search" margin={margin}>
                                    {field}
                                </TitledPanel>
                                :
                                field
                            }
                            {isOpen && inputValue.length > 1 && results.length > 0 ?
                                <List data-test="list-results" {...getMenuProps()} dense className={classes.results} style={wrapped ?
                                    {
                                        marginTop: '-10px',
                                        marginLeft: '15px',
                                        width: 'calc(100% - 32px)'
                                    }
                                    :
                                    {}}>
                                    {
                                        results.sort(
                                            function (a, b) {
                                                const sortProp1 = Array.isArray(sortProp) ? sortProp[0] : sortProp;
                                                const sortProp2 = Array.isArray(sortProp) && sortProp.length === 2 ? sortProp[1] : null;
                                                // Sort by length first
                                                if (sortByLength) {
                                                    const lenA = sortProp1 && a[sortProp1] && a[sortProp1][lang] ?
                                                        a[sortProp1][lang].length
                                                        :
                                                        (sortProp2 && a[sortProp2] && a[sortProp2][lang] ? a[sortProp2][lang].length : 0);
                                                    const lenB = sortProp1 && b[sortProp1] && b[sortProp1][lang] ?
                                                        b[sortProp1][lang].length
                                                        :
                                                        (sortProp2 && b[sortProp2] && b[sortProp2][lang] ? b[sortProp2][lang].length : 0);
                                                    if (lenA < lenB) return -1;
                                                    if (lenA > lenB) return 1;
                                                }
                                                // Then sort by prop value
                                                const nameA = sortProp1 && a[sortProp1] && a[sortProp1][lang] ?
                                                    a[sortProp1][lang].toString().toUpperCase()
                                                    :
                                                    (sortProp2 && a[sortProp2] && a[sortProp2][lang] ? a[sortProp2][lang].toString().toUpperCase() : "");
                                                const nameB = sortProp1 && b[sortProp1] && b[sortProp1][lang] ?
                                                    b[sortProp1][lang].toString().toUpperCase()
                                                    :
                                                    (sortProp2 && b[sortProp2] && b[sortProp2][lang] ? b[sortProp2][lang].toString().toUpperCase() : "");
                                                if (nameA < nameB) return -1;
                                                if (nameB < nameA) return 1;
                                                // Finally sort by prop names
                                                const propA = sortProp1 && a[sortProp1] ?
                                                    sortProp1
                                                    :
                                                    (sortProp2 && a[sortProp2] ? sortProp2 : "");
                                                const propB = sortProp1 && b[sortProp1] ?
                                                    sortProp1
                                                    :
                                                    (sortProp2 && b[sortProp2] ? sortProp2 : "");
                                                if (propA < propB) return -1;
                                                if (propB < propA) return 1;
                                                return 0;
                                            }
                                        ).map((result, index) => {
                                            let clickable = true;
                                            if (isClickableResult) {
                                                clickable = isClickableResult(result);
                                            }
                                            if (clickable) {
                                                return <ListItem
                                                    data-test={"list-item-" + index}
                                                    className={classes.result}
                                                    {...getItemProps({
                                                        key: result.id,
                                                        index,
                                                        item: result,
                                                        style: {
                                                            backgroundColor: highlightedIndex === index ? '#e5e5e5' : 'white'
                                                        }
                                                    })}>
                                                    {listIcon && <ListItemIcon className={classes.resultIcon}>{listIcon(result, lang)}</ListItemIcon>}
                                                    <ListItemText disableTypography data-test="list-item-text" primary={listDisplay(result, lang)} />
                                                    {listSecondIcon && <ListItemSecondaryAction className={classes.resultSecondIcon}>{listSecondIcon(result, lang)}</ListItemSecondaryAction>}
                                                </ListItem>;
                                            }
                                            else {
                                                return <ListItem
                                                    data-test={"list-item-" + index + "-invalid"}
                                                    className={classes.invalidResult}
                                                    key={result.id}>
                                                    {listIcon && <ListItemIcon className={classes.resultIcon}>{listIcon(result, lang)}</ListItemIcon>}
                                                    <ListItemText data-test="list-item-text" primary={listDisplay(result, lang)} />
                                                    {listSecondIcon && <ListItemSecondaryAction className={classes.resultSecondIcon}>{listSecondIcon(result, lang)}</ListItemSecondaryAction>}
                                                </ListItem>;

                                            }
                                        })}
                                </List>
                                :
                                !isOpen && inputValue.length > 1 && results && results.length === 0 ?
                                    <div data-test="no-results" />
                                    :
                                    null}
                        </div>
                    );
                }}
            </Downshift >
        );
    }
}

SearchDropDown.defaultProps = {
    focus: false,
    wrapped: false,
    margin: false,
    suppressMessages: false,
    errorText: null,
    maxLength: 50,
    sortByLength: true
};

SearchDropDown.propTypes = {
    dataTest: PropTypes.string,
    lang: PropTypes.string,
    maxLength: PropTypes.number,
    focus: PropTypes.bool,
    margin: PropTypes.bool,
    isClickableResult: PropTypes.func,
    chosen: PropTypes.func.isRequired,
    search: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    listIcon: PropTypes.func,
    listSecondIcon: PropTypes.func,
    sortByLength: PropTypes.bool,
    sortProp: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array
    ]),
    wrapped: PropTypes.bool,
    onClearResults: PropTypes.func,
    errorText: PropTypes.string,
    onHandleSelectionChange: PropTypes.func,
    onNoResults: PropTypes.func,
    suppressMessages: PropTypes.bool,
    listSelectionDisplay: PropTypes.func,
    listDisplay: PropTypes.func.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(SearchDropDown);